var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import { autorun, toJS } from "mobx";
import { types, getParentOfType, getSnapshot, applySnapshot, onSnapshot as _onSnapshot } from "mobx-state-tree";
import { get, trim, isNil } from "lodash-es";
import { generateId, getFormattedValue, getNewColumnName, hasCircularDependencyTableColumn, isFormula, mapColumnsToCells, reorderArrayElements, standardizeInput, validateNodeName } from "../core/utils";
import { columnCellsTypes, errors, NODE_NAME_MAX_LENGTH, NODE_TYPES } from "../core/common";
import { simplifyTokens, tokenize } from "../core/parser";
import { Store, ValidationError } from "./Store";

export var ColumnModel = types.model("ColumnModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  name: types.string,
  type: NODE_TYPES.column,
  columnValue: types.frozen(null),
  footerValue: types.frozen(null),
  footerFormatted: types.frozen(null),
  formatter: types.maybeNull(types.string),
  columnCellsType: types.frozen(null),
  width: 150
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },
    get table() {
      return getParentOfType(self, TableModel);
    },
    get hasColumnValue() {
      return !isNil(self.columnValue);
    },
    get isColumnValueFormula() {
      return self.hasColumnValue && isFormula(self.columnValue);
    },
    get hasColumnFooter() {
      return !isNil(self.footerValue);
    },
    get isColumnFooterFormula() {
      return self.hasColumnFooter && isFormula(self.footerValue);
    },
    get footerParsed() {
      if (!self.isColumnFooterFormula) {
        return { result: self.footerValue, error: null };
      }

      var tokens = simplifyTokens(tokenize(self.footerValue), self.store);

      return self.store.parser.parseAsTokens(tokens);
    },
    get _footerFormatted() {
      var _self$footerParsed = self.footerParsed,
          result = _self$footerParsed.result,
          error = _self$footerParsed.error;


      if (error) {
        return error;
      }

      return getFormattedValue(result, self.formatter);
    },
    get index() {
      return self.table.columns.findIndex(function (c) {
        return c.id === self.id;
      });
    },
    getColumnCells: function getColumnCells() {
      return self.table.rows.reduce(function (acc, row) {
        var cell = row.findCellByColumnId(self.id);
        acc.push(cell);
        return acc;
      }, []);
    },
    getColumnCellByRowIndex: function getColumnCellByRowIndex(rowIndex) {
      if (rowIndex >= self.table.rows.length) {
        return undefined;
      }

      return self.table.rows[rowIndex].findCellByColumnId(self.id);
    }
  };
}).actions(function (self) {
  var footerDisposer = void 0;
  var footerFormattedDisposer = void 0;

  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var newName = trim(_name);

      if (oldName === newName) {
        return;
      }

      if (!newName) {
        throw new ValidationError("column_name", errors.column_empty_name);
      }

      var columnNameExists = self.table.columns.some(function (_ref) {
        var name = _ref.name;
        return name === newName;
      });
      if (columnNameExists) {
        throw new ValidationError("column_name", errors.column_name_exists);
      }

      if (!/^[\w%\[\]\(\)\$ ]+$/.test(newName)) {
        throw new ValidationError("column_name", // ToDo: extract constants
        errors.column_name_not_allowed);
      }

      if (newName.length > 16) {
        throw new ValidationError("column_name", errors.column_name_too_long);
      }

      self.name = newName;
      self.table.store.renameColumn(self.table, self, oldName, self.name);
    },
    setColumnValue: function setColumnValue(value) {
      if (isFormula(value) && hasCircularDependencyTableColumn(self.name, value, self.table.name)) {
        throw new ValidationError("column_formula", // ToDo: change to column_value
        errors.column_formula_circular_dependency);
      }

      self.columnValue = value;
      self.columnCellsType = null;
    },
    removeColumnValue: function removeColumnValue() {
      self.columnValue = null;
    },
    setFormatter: function setFormatter(formatter) {
      self.formatter = formatter;
    },
    setWidth: function setWidth(width) {
      self.width = width;
    },
    setFooterValue: function setFooterValue(footerValue) {
      self.footerValue = footerValue;
    },
    setFooterFormatted: function setFooterFormatted(footerFormatted) {
      self.footerFormatted = footerFormatted;
    },
    setColumnCellsType: function setColumnCellsType() {
      var columnCellsType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

      self.removeColumnValue();
      self.columnCellsType = columnCellsType;
    },
    setColumnCellsTypeAsSelect: function setColumnCellsTypeAsSelect() {
      self.setColumnCellsType(columnCellsTypes.select);
    },
    addOptionToSelect: function addOptionToSelect(value) {
      self.columnCellsType = _extends({}, self.columnCellsType, {
        config: _extends({}, self.columnCellsType.config, {
          values: [].concat(self.columnCellsType.config.values, [{ value: value }])
        })
      });
    },
    removeColumnType: function removeColumnType() {
      self.columnCellsType = null;
      self.columnValue = null;
    },
    remove: function remove() {
      self.table.removeColumn(self.id);
    },
    afterCreate: function afterCreate() {
      footerDisposer = autorun(function () {
        if (!self.table.showFooter) {
          self.setFooterValue(null);
        }
      });

      footerFormattedDisposer = autorun(function () {
        self.setFooterFormatted(self._footerFormatted);
      });
    },
    beforeDestroy: function beforeDestroy() {
      if (footerDisposer) {
        footerDisposer();
      }

      if (footerFormattedDisposer) {
        footerFormattedDisposer();
      }
    }
  };
});

export var CellModel = types.model("CellModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: NODE_TYPES.cell,
  column: types.safeReference(ColumnModel),
  value: types.frozen(null),
  formatted: types.frozen(null)
}).views(function (self) {
  return {
    get row() {
      return getParentOfType(self, RowModel);
    },
    get index() {
      return self.row.cells.findIndex(function (_ref2) {
        var id = _ref2.id;
        return id === self.id;
      });
    },
    get rowIndex() {
      return self.table.rows.findIndex(function (_ref3) {
        var id = _ref3.id;
        return id === self.row.id;
      });
    },
    get table() {
      return self.row.table;
    },
    get parsed() {
      var _self$column = self.column,
          hasColumnValue = _self$column.hasColumnValue,
          isColumnValueFormula = _self$column.isColumnValueFormula,
          columnValue = _self$column.columnValue;


      if (!hasColumnValue) {
        return { result: self.value, error: null };
      }

      if (!isColumnValueFormula) {
        return { result: columnValue, error: null };
      }

      // Replicating Coda / AirTable behaviour here
      // If cell refers to other column (i.e. table1[Column1])
      // it takes the value of the cell from the same row
      // ToDo: test if works for more complicated formulas like table1[[foo]:[bar]]
      var tokens = simplifyTokens(tokenize(columnValue), self.column.store, self.rowIndex, self.table.name);

      return self.table.store.parser.parseAsTokens(tokens);
    },
    get _formatted() {
      if (!self.column.hasColumnValue) {
        return getFormattedValue(self.value, self.column.formatter);
      }

      var _self$parsed = self.parsed,
          result = _self$parsed.result,
          error = _self$parsed.error;


      if (error) {
        return error;
      }

      return getFormattedValue(result, self.column.formatter);
    }
  };
}).actions(function (self) {
  var valueDisposer = void 0;
  var formattedDisposer = void 0;

  return {
    setValue: function setValue(value) {
      self.value = standardizeInput(value);
    },
    setFormatted: function setFormatted(formatted) {
      self.formatted = formatted;
    },
    afterCreate: function afterCreate() {
      if (!self.column) {
        self.column = self.table.columns[self.index];
      }

      valueDisposer = autorun(function () {
        // detect when column value is set and reset cell
        if (self.column.hasColumnValue) {
          self.setValue(null);
        }
      });

      formattedDisposer = autorun(function () {
        self.setFormatted(self._formatted);
      });
    },
    beforeDestroy: function beforeDestroy() {
      valueDisposer();
      formattedDisposer();
    }
  };
});

export var RowModel = types.model("RowModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: NODE_TYPES.row,
  cells: types.array(CellModel),
  height: 27
}).views(function (self) {
  return {
    get table() {
      return getParentOfType(self, TableModel);
    },
    findCellByColumnId: function findCellByColumnId(columnId) {
      return self.cells.find(function (_ref4) {
        var column = _ref4.column;
        return column.id === columnId;
      });
    },
    findCellById: function findCellById(cellId) {
      return self.cells.find(function (_ref5) {
        var id = _ref5.id;
        return id === cellId;
      });
    }
  };
}).actions(function (self) {
  return {
    setHeight: function setHeight(height) {
      self.height = height;
    }
  };
});

export var TableModel = types.model("TableModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: NODE_TYPES.table,
  name: types.string,
  columns: types.array(ColumnModel),
  rows: types.array(RowModel),
  showFooter: false
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },
    getColumnById: function getColumnById(id) {
      return self.columns.find(function (c) {
        return c.id === id;
      });
    },
    getColumnByName: function getColumnByName(name) {
      return self.columns.find(function (c) {
        return c.name === name;
      });
    },
    getColumnIndexById: function getColumnIndexById(id) {
      return self.columns.findIndex(function (c) {
        return c.id === id;
      });
    },

    serialize: function serialize() {
      var toJSON = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      return toJSON ? getSnapshot(self) : toJS(self);
    },
    onSnapshot: function onSnapshot(callback) {
      return _onSnapshot(self, callback);
    }
  };
}).actions(function (self) {
  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var name = trim(_name);

      if (oldName === name) {
        return;
      }

      if (get(name, "length") > NODE_NAME_MAX_LENGTH) {
        throw new Error(errors.name_too_long);
      }

      if (!validateNodeName(name)) {
        throw new Error(errors.name_invalid);
      }

      var existingNode = self.store.findByName(name);
      if (existingNode && existingNode.id !== self.id) {
        throw new Error(errors.name_exists);
      }

      self.name = name;
      self.store.renameNode(self, oldName, name);
    },
    addColumn: function addColumn() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.columns.length;
      var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!props.name) {
        props.name = getNewColumnName(self);
      }

      var column = ColumnModel.create(props);
      self.columns.splice(index, 0, column);
      self.rows.forEach(function (_ref6) {
        var cells = _ref6.cells;

        cells.splice(index, 0, { column: column.id });
      });
    },
    removeColumn: function removeColumn(id) {
      if (self.columns.length === 1 && self.columns[0].id === id) {
        self.destroy();
        return;
      }

      var columnIndex = self.getColumnIndexById(id);
      self.rows.forEach(function (_ref7) {
        var cells = _ref7.cells;

        cells.splice(columnIndex, 1);
      });
      self.columns.splice(columnIndex, 1);
    },
    reorderColumns: function reorderColumns(oldIndex, newIndex, length) {
      self.columns = reorderArrayElements(self.columns, oldIndex, newIndex, length);
      self.syncCellsOrderWithColumns();
    },
    syncCellsOrderWithColumns: function syncCellsOrderWithColumns() {
      self.rows.forEach(function (row, i) {
        var orderedCells = self.columns.map(function (col) {
          return col.getColumnCellByRowIndex(i);
        });
        row.cells.replace(orderedCells);
      });
    },
    insertRow: function insertRow() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.rows.length;
      var row = arguments[1];

      self.rows.splice(index, 0, row || { cells: mapColumnsToCells(self.columns) });
    },
    removeRow: function removeRow() {
      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : self.rows.length - 1;

      // ToDo: improve behaviour when removing last row (remove whole table like in case of column?)
      if (self.rows.length === 1 || index >= self.rows.length) {
        return;
      }

      self.rows.splice(index, 1);
    },
    toggleFooter: function toggleFooter() {
      var showFooter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.showFooter;

      self.showFooter = showFooter;
    },
    destroy: function destroy() {
      self.store.removeNode(self);
    },
    setProps: function setProps(props) {
      if (!props) return;

      applySnapshot(self, _extends({}, self.serialize(), props));
    }
  };
});